import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, from, of, firstValueFrom } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { GoogleMapsLoaderService } from './google-maps-loader.service';
import { ApiService } from './api.service';

const CALIFORNIA_BOUNDS: google.maps.LatLngBoundsLiteral = {
  north: 42.0095,
  south: 32.5295,
  east: -114.1312,
  west: -124.6509,
};

export interface ZipLookupResult {
  city: string;
  state: string;
  county: string;
  zipCode: string;
  streetAddress: string;
  unit: string;
  bounds: google.maps.LatLngBoundsLiteral | null;
  /** Only set for a picked street-address suggestion (getAddressDetails) — the exact
   *  point of that address. Left unset for ZIP/region lookups, whose point is just a
   *  centroid and must not be stored as the property's location. */
  latitude?: number;
  longitude?: number;
}

/** Place types that identify one specific address rather than a ZIP/city/region. */
const STREET_LEVEL_TYPES = ['street_address', 'premise', 'subpremise'];

export interface AddressSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
  /** Not returned by Autocomplete predictions — filled in afterwards via
   *  attachZipCodes(), one Place Details call per suggestion. */
  zipCode?: string;
}

interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GoogleGeocodeResult {
  address_components: GoogleAddressComponent[];
  geometry?: {
    location: { lat: number; lng: number };
    bounds?: { northeast: { lat: number; lng: number }; southwest: { lat: number; lng: number } };
  };
}

interface GoogleGeocodeResponse {
  status: string;
  results: GoogleGeocodeResult[];
}

@Injectable({ providedIn: 'root' })
export class GeocodingService {
  private readonly api = inject(ApiService);
  private readonly mapsLoader = inject(GoogleMapsLoaderService);
  private geocoder: google.maps.Geocoder | null = null;

  // Places API (New) — the legacy AutocompleteService/PlacesService are denied on
  // projects that only have "Places API (New)" enabled.
  private placesLib: google.maps.PlacesLibrary | null = null;
  private sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
  private readonly predictions = new Map<string, google.maps.places.PlacePrediction>();
  private placesLoadPromise: Promise<boolean> | null = null;
  private apiKeyPromise: Promise<string | null> | null = null;

  lookupZipCode(zipCode: string): Observable<ZipLookupResult | null> {
    return this.geocodeAddressDetails(zipCode);
  }

  reverseGeocode(lat: number, lng: number): Observable<ZipLookupResult | null> {
    return this.geocode({ location: { lat, lng } }).pipe(
      map(response => this.preferStreetAddressResult(response)),
      switchMap(response => this.toZipLookupResultWithFallback(response)),
      catchError(() => of(null)),
    );
  }

  /** Geocoding through the Maps JavaScript SDK (google.maps.Geocoder) rather than the
   *  maps.googleapis.com/maps/api/geocode web service: that web service rejects keys
   *  restricted by HTTP referrer ("API keys with referer restrictions cannot be used with
   *  this API"), which is exactly how the public browser key has to be locked down. The SDK
   *  geocoder is the same Google Geocoding service and works with a referrer-restricted
   *  key. Results are mapped to the same shape the REST response had, so the callers below
   *  are unchanged. */
  private geocode(request: google.maps.GeocoderRequest): Observable<GoogleGeocodeResponse> {
    return from(this.getGeocoder()).pipe(
      switchMap(geocoder => {
        if (!geocoder) return of<GoogleGeocodeResponse>({ status: 'REQUEST_DENIED', results: [] });
        return from(geocoder.geocode(request)).pipe(
          map(res => ({ status: 'OK', results: res.results.map(r => this.fromSdkResult(r)) })),
        );
      }),
      // The SDK rejects (instead of returning a status) when nothing matches or on an error.
      catchError(() => of<GoogleGeocodeResponse>({ status: 'ZERO_RESULTS', results: [] })),
    );
  }

  private async getGeocoder(): Promise<google.maps.Geocoder | null> {
    if (this.geocoder) return this.geocoder;
    const key = await this.getApiKey();
    if (!key) return null;
    const g = await this.mapsLoader.load(key, ['places']);
    const { Geocoder } = await g.maps.importLibrary('geocoding') as google.maps.GeocodingLibrary;
    this.geocoder = new Geocoder();
    return this.geocoder;
  }

  private fromSdkResult(r: google.maps.GeocoderResult): GoogleGeocodeResult {
    const loc = r.geometry?.location;
    const b = r.geometry?.bounds;
    return {
      address_components: r.address_components.map(c => ({
        long_name: c.long_name, short_name: c.short_name, types: c.types,
      })),
      geometry: loc
        ? {
          location: { lat: loc.lat(), lng: loc.lng() },
          bounds: b
            ? {
              northeast: { lat: b.getNorthEast().lat(), lng: b.getNorthEast().lng() },
              southwest: { lat: b.getSouthWest().lat(), lng: b.getSouthWest().lng() },
            }
            : undefined,
        }
        : undefined,
    };
  }

  /** Same browser-restricted Maps key GoogleMapsLoaderService uses for the admin
   *  map/Street View, fetched from the backend instead of baked into the bundle
   *  — see LookupController.getPublicConfig. Cached after the first call. */
  private getApiKey(): Promise<string | null> {
    if (!this.apiKeyPromise) {
      this.apiKeyPromise = firstValueFrom(this.api.get<{ mapsApiKey: string | null }>('/public/config'))
        .then(res => res.mapsApiKey)
        .catch(() => null);
    }
    return this.apiKeyPromise;
  }

  private preferStreetAddressResult(response: GoogleGeocodeResponse): GoogleGeocodeResponse {
    if (response.status !== 'OK' || response.results.length < 2) {
      return response;
    }
    const hasStreet = (result: GoogleGeocodeResult) => {
      const types = result.address_components.flatMap(c => c.types);
      return types.includes('street_number') && types.includes('route');
    };
    const best = response.results.find(hasStreet);
    if (!best || best === response.results[0]) {
      return response;
    }
    return { ...response, results: [best, ...response.results.filter(r => r !== best)] };
  }

  searchAddresses(
    query: string,
    bounds?: google.maps.LatLngBoundsLiteral | null,
    typesOverride?: string[],
  ): Observable<AddressSuggestion[]> {
    const trimmed = (query ?? '').trim();
    if (trimmed.length < 3) {
      return of([]);
    }

    // The new API has no 'address' collection — street-level place types instead.
    const types = (typesOverride ?? (/^\d+$/.test(trimmed) ? ['(regions)'] : ['address']))
      .flatMap(t => (t === 'address' ? ['street_address', 'premise', 'subpremise'] : [t]));

    return from(this.ensurePlacesLibrary()).pipe(
      switchMap(ready => {
        const lib = this.placesLib;
        if (!ready || !lib) {
          return of<AddressSuggestion[]>([]);
        }
        // One session token across the keystrokes of a search, ended by the Place
        // Details call on selection (see getAddressDetails) — Google bills it as one session.
        this.sessionToken ??= new lib.AutocompleteSessionToken();
        return from(lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: trimmed,
          includedRegionCodes: ['us'],
          includedPrimaryTypes: types,
          locationRestriction: bounds ?? CALIFORNIA_BOUNDS,
          sessionToken: this.sessionToken,
        })).pipe(
          map(({ suggestions }) => suggestions
            .filter(s => !!s.placePrediction)
            .map(s => {
              const p = s.placePrediction!;
              this.predictions.set(p.placeId, p);
              return {
                placeId: p.placeId,
                description: p.text.text,
                mainText: p.mainText?.text ?? p.text.text,
                secondaryText: p.secondaryText?.text ?? '',
              };
            })),
        );
      }),
      catchError(() => of([])),
    );
  }

  /** Autocomplete predictions never include the postal code, so callers that want
   *  to show or filter on it fetch it separately (one Place Details call per
   *  suggestion, run in parallel). */
  attachZipCodes(suggestions: AddressSuggestion[]): Observable<AddressSuggestion[]> {
    if (!suggestions.length) {
      return of(suggestions);
    }
    return from(this.ensurePlacesLibrary()).pipe(
      switchMap(ready => {
        if (!ready || !this.placesLib) {
          return of(suggestions);
        }
        return forkJoin(
          suggestions.map(suggestion =>
            this.getSuggestionZipCode(suggestion.placeId).pipe(
              map(zipCode => ({ ...suggestion, zipCode: zipCode ?? undefined })),
            ),
          ),
        );
      }),
    );
  }

  private getSuggestionZipCode(placeId: string): Observable<string | null> {
    return from(new this.placesLib!.Place({ id: placeId }).fetchFields({ fields: ['addressComponents'] })).pipe(
      map(({ place }) => place.addressComponents?.find(c => c.types.includes('postal_code'))?.longText ?? null),
      catchError(() => of(null)),
    );
  }

  getAddressDetails(placeId: string): Observable<ZipLookupResult | null> {
    if (!placeId) {
      return of(null);
    }

    return from(this.ensurePlacesLibrary()).pipe(
      switchMap(ready => {
        const lib = this.placesLib;
        if (!ready || !lib) {
          return of<ZipLookupResult | null>(null);
        }
        // toPlace() carries the autocomplete session, so this call ends it; start a
        // fresh session for the next search.
        const prediction = this.predictions.get(placeId);
        const place = prediction ? prediction.toPlace() : new lib.Place({ id: placeId });
        this.sessionToken = null;
        this.predictions.clear();

        return from(place.fetchFields({ fields: ['addressComponents', 'location', 'viewport', 'types'] })).pipe(
          switchMap(({ place: details }) => {
            if (!details.addressComponents) {
              return of<ZipLookupResult | null>(null);
            }
            const location = details.location;
            const viewport = details.viewport;
            const geocodeResult: GoogleGeocodeResult = {
              address_components: details.addressComponents.map(c => ({
                long_name: c.longText ?? '', short_name: c.shortText ?? '', types: c.types,
              })),
              geometry: location
                ? {
                  location: { lat: location.lat(), lng: location.lng() },
                  bounds: viewport
                    ? {
                      northeast: { lat: viewport.getNorthEast().lat(), lng: viewport.getNorthEast().lng() },
                      southwest: { lat: viewport.getSouthWest().lat(), lng: viewport.getSouthWest().lng() },
                    }
                    : undefined,
                }
                : undefined,
            };
            const isStreetLevel = !!location && (details.types ?? []).some(t => STREET_LEVEL_TYPES.includes(t));
            return this.toZipLookupResultWithFallback({ status: 'OK', results: [geocodeResult] }).pipe(
              map(result => (result && isStreetLevel
                ? { ...result, latitude: location!.lat(), longitude: location!.lng() }
                : result)),
            );
          }),
        );
      }),
      catchError(() => of(null)),
    );
  }

  private ensurePlacesLibrary(): Promise<boolean> {
    if (this.placesLib) {
      return Promise.resolve(true);
    }
    if (!this.placesLoadPromise) {
      this.placesLoadPromise = this.getApiKey()
        .then(apiKey => {
          if (!apiKey) return false;
          return this.mapsLoader.load(apiKey, ['places']).then(async g => {
            this.placesLib = await g.maps.importLibrary('places') as google.maps.PlacesLibrary;
            return true;
          });
        })
        .catch(() => false);
    }
    return this.placesLoadPromise;
  }

  private geocodeAddressDetails(query: string): Observable<ZipLookupResult | null> {
    const trimmed = (query ?? '').trim();
    if (!trimmed) {
      return of(null);
    }

    return this.geocode({ address: trimmed, componentRestrictions: { country: 'US', administrativeArea: 'CA' } }).pipe(
      switchMap(response => this.toZipLookupResultWithFallback(response)),
      catchError(() => of(null)),
    );
  }

  private toZipLookupResultWithFallback(response: GoogleGeocodeResponse): Observable<ZipLookupResult | null> {
    const result = this.toZipLookupResult(response);
    if (!result) {
      return of(null);
    }
    const location = response.results[0]?.geometry?.location;
    if (result.zipCode || !location) {
      return of(result);
    }
    return this.lookupZipAtLocation(location).pipe(
      map(resolvedZip => ({ ...result, zipCode: resolvedZip })),
    );
  }

  private lookupZipAtLocation(location: { lat: number; lng: number }): Observable<string> {
    return this.geocode({ location }).pipe(
      map(response => {
        for (const result of response.results) {
          const zip = result.address_components.find(c => c.types.includes('postal_code'))?.long_name;
          if (zip) return zip;
        }
        return '';
      }),
      catchError(() => of('')),
    );
  }

  private toZipLookupResult(response: GoogleGeocodeResponse): ZipLookupResult | null {
    if (response.status !== 'OK' || !response.results.length) {
      return null;
    }
    return this.parseResult(response.results[0]);
  }

  private parseResult(result: GoogleGeocodeResult): ZipLookupResult | null {
    const components = result.address_components;
    const findByType = (type: string) => components.find(c => c.types.includes(type));

    const city = findByType('locality')?.long_name
      ?? findByType('sublocality')?.long_name
      ?? findByType('administrative_area_level_3')?.long_name
      ?? findByType('neighborhood')?.long_name;
    const state = findByType('administrative_area_level_1')?.short_name;
    if (!city || !state) {
      return null;
    }

    const county = (findByType('administrative_area_level_2')?.long_name ?? '').replace(/\s+County$/i, '');
    const zipCode = findByType('postal_code')?.long_name ?? '';
    const streetNumber = findByType('street_number')?.long_name ?? '';
    const route = findByType('route')?.long_name ?? '';
    const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
    const unit = findByType('subpremise')?.long_name ?? '';

    const geoBounds = result.geometry?.bounds;
    const bounds: google.maps.LatLngBoundsLiteral | null = geoBounds
      ? { north: geoBounds.northeast.lat, south: geoBounds.southwest.lat, east: geoBounds.northeast.lng, west: geoBounds.southwest.lng }
      : null;

    return { city, state, county, zipCode, streetAddress, unit, bounds };
  }
}
